import { ChartingErrorBoundary } from '../../atoms';
import {
  GlobalPassthroughs,
  TableDashletConnected,
  Series,
  SimulationSeries,
} from '../../../types';
import { TableDashlet } from '../../molecules';
import {
  DSTreeObject,
  keyforSimulation,
  Resolutions,
  useIsFeatureEnabled,
  useSelectSensors,
  useSettings,
} from '@innovyze/stylovyze';
import * as InsightChart from '../../../_next/core/_insight-chart';
import * as SeriesData from '../../../_next/core/series-data';
import * as TimeSeriesData from '../../../_next/core/time-series-data';
import * as SimulationsData from '../../../_next/core/simulations-data';
import React, { useEffect } from 'react';
import useSensorData from '../../../core/hooks/useSensorData';
import {
  SensorDataRecords,
  SensorMeasureData,
} from '../../../core/types/data.types';
import { fixCollectionInterval } from '../../../_next/core/time-series-data/utils';
import { isSensorSeries, isSimulationSeries } from '../../../utils';
import { Reading } from '../../../core/types/reading.types';
import { getTableRequestChunks } from '../../../utils/getTableRequestChunks';
import { AxiosResponse } from 'axios';

type EdgeData = [
  open: number | null,
  close: number | null,
  low: number | null,
  high: number | null,
  average: number | null,
  sum: number | null,
];

type SensorSource = {
  sensorId: string;
  resolution: InsightChart.Resolution;
  readings: InsightChart.Reading[];
};

type SimulationSource = SimulationSeries;

export type EdgeSource = SensorSource | SimulationSource;

type ConnectedTableDashletProps = TableDashletConnected & GlobalPassthroughs;

function isSensorSource(source: EdgeSource): source is SensorSource {
  return 'sensorId' in source;
}

function isSimulationSource(source: EdgeSource): source is SimulationSource {
  return 'database_id' in source && 'object_id' in source;
}

function simulationSourceStringifier(source: SimulationSource): string {
  const { database_id, result_id, table_id, object_id } = source;
  const resolution = Resolutions.RAW;
  const analyticFunction = Reading.Close.toLowerCase();
  return `${object_id}:${resolution}:type:${analyticFunction}:db-${database_id}:re-${result_id}:t-${table_id}`;
}

function edgeSourceStringifier(source: EdgeSource): string {
  if (isSensorSource(source)) {
    return `${source.sensorId}:${source.resolution}:${source.readings.join(',')}`;
  }
  return simulationSourceStringifier(source);
}

function limit(value: string | null | undefined, defaultValue = 500): number {
  if (typeof value === 'string' || value === null) return defaultValue;
  if (isNaN(Number(value))) return defaultValue;
  return Number(value);
}

function getSimulationOname(
  simulationId: string,
  simulations: DSTreeObject[]
): string {
  return (
    simulations.find((sim) => sim.oid.toString() === simulationId)?.oname ?? ''
  );
}

function getLatestSimulationId(
  oname: string,
  simulations: DSTreeObject[]
): string | null {
  let latestSimulation: DSTreeObject | null = null;
  let latestTime = -1;

  for (const simulation of simulations) {
    if (simulation.oname === oname && simulation.crtime > latestTime) {
      latestSimulation = simulation;
      latestTime = simulation.crtime;
    }
  }
  return latestSimulation?.oid.toString() ?? null;
}

const isSimulationDeleted = (
  simulationId: string,
  simulations: DSTreeObject[]
): boolean => {
  const isDeleted = !simulations.some(
    (sim) => sim.oid.toString() === simulationId
  );
  return isDeleted;
};

const readingIndex = {
  open: 0,
  close: 1,
  low: 2,
  high: 3,
  average: 4,
  sum: 5,
};

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * Version Switch
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

const ConnectedTableDashlet = (
  props: ConnectedTableDashletProps
): React.ReactElement => {
  const isV2Enabled = useIsFeatureEnabled('info-360-analytics-hp2-charts');

  if (!isV2Enabled) {
    return <TableDashletV1 {...props} />;
  }

  return <TableDashletV2 {...props} />;
};

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * New Table
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

export const TableDashletV2 = (
  props: ConnectedTableDashletProps
): JSX.Element | null => {
  const { companySettings } = useSettings();
  const { sensors, initialized: sensorsInitialized } = useSelectSensors();
  const useV3 = useIsFeatureEnabled('info-360-edge-analytics-parquet-files');
  const dataLimit = useIsFeatureEnabled('info-360-analytics-hp2-charts-limit');
  const isEdgeEnabled = useIsFeatureEnabled('info-360-edge-analytics-table');
  const seriesArray = React.useMemo(
    () => (!Array.isArray(props.series) ? [props.series] : props.series),
    [props.series]
  );

  const sensorsSeriesArray = React.useMemo(
    () =>
      seriesArray.filter(
        (series): series is Series =>
          isSensorSeries(series) && !series.customData
      ),
    [seriesArray]
  );

  const seriesData = InsightChart.useSensorDataDisplay(sensorsSeriesArray);

  const { retrieve } = seriesData;

  //

  const sensorsRef = React.useRef(sensors);
  sensorsRef.current = sensors;

  const [edgeSources] = SeriesData.useSources<EdgeSource>(() => {
    if (!seriesArray?.length) return [];
    return seriesArray?.reduce((a, s) => {
      if (isSensorSeries(s) && !s.customData) {
        a.push({
          sensorId: s.sensorId,
          resolution: s.resolution,
          readings: props.readings ?? ['Close'],
        });
      }
      if (isSimulationSeries(s)) {
        a.push({ ...s });
      }
      return a;
    }, [] as EdgeSource[]);
  }, [props.series, props.readings]);

  const [simulations, simulationsStatus, retrieveSimulations] =
    SeriesData.useRetriever<DSTreeObject[]>(
      async (signal) => {
        const simulationSources = edgeSources.filter(isSimulationSource) ?? [];
        if (!simulationSources.length) {
          return;
        }
        const pairsMap = new Map<string, [string, string]>();
        simulationSources.forEach(({ analysis_group_id, database_id }) => {
          const key = keyforSimulation(database_id, analysis_group_id);
          pairsMap.set(key, [database_id, analysis_group_id]);
        });
        const requests = Array.from(pairsMap.values()).map(
          ([dbid, analysisGroupId]) =>
            SimulationsData.retrieve(signal, dbid, analysisGroupId)
        );
        const responses = await Promise.all(requests);
        const results = responses
          .map((response) => response.data?.treeobjects)
          .flat();
        return results;
      },
      [edgeSources]
    );

  useEffect(() => {
    if (!edgeSources.length) {
      return;
    }
    retrieveSimulations();
  }, [retrieveSimulations, edgeSources]);

  // Create a mapping between original and updated simulation sources
  const simulationSourceMapping = React.useMemo(() => {
    const mapping = new Map<string, SimulationSource>();
    edgeSources.filter(isSimulationSource).forEach((originalSource) => {
      if (!simulations?.length) return;

      // Check if the original simulation still exists
      if (isSimulationDeleted(originalSource.result_id, simulations)) {
        return;
      }

      const oname = getSimulationOname(originalSource.result_id, simulations);
      if (!oname) {
        return;
      }

      const latestSimulationId = getLatestSimulationId(oname, simulations);
      if (!latestSimulationId) {
        return;
      }
      const updatedSource = {
        ...originalSource,
        result_id: latestSimulationId,
      };
      mapping.set(originalSource.simulation_name, updatedSource);
    });
    return mapping;
  }, [edgeSources, simulations]);

  const [edgeData, edgeStatus, retrieveEdgeData] = SeriesData.useRetriever<
    TimeSeriesData.ResponseBody,
    { timeSelection?: TimeSeriesData.PartialTimeSelection }
  >(
    async (signal, params) => {
      if (!edgeSources?.length) return;
      const timeSelection = {
        from: params.timeSelection?.from ?? 'oldest',
        to: params.timeSelection?.to ?? 'latest',
      } as TimeSeriesData.TimeSelection;

      const sensorSources = edgeSources.filter(isSensorSource);
      const simulationSources = Array.from(
        simulationSourceMapping.values()
      ).filter(({ result_id }) => !isSimulationDeleted(result_id, simulations));

      const baseRequestBody: Omit<TimeSeriesData.RequestBody, 'sources'> = {
        order: 'asc',
        timeSelection,
        limit: limit(typeof dataLimit === 'string' ? dataLimit : null),
        timeZone: companySettings.timeZoneIANA,
        data_version: useV3 ? 'v3.0' : 'v2',
        snapping: 'oldest',
      };

      let sensorRequests: Promise<
        AxiosResponse<TimeSeriesData.ResponseBody, unknown>
      >[] = [];

      let simulationRequests: Promise<
        AxiosResponse<TimeSeriesData.ResponseBody, unknown>
      >[] = [];

      if (sensorSources.length) {
        sensorRequests = getTableRequestChunks(sensorSources).map((sources) =>
          TimeSeriesData.retrieve(signal, {
            ...baseRequestBody,
            sources: sources.map((s) => {
              const source = s as SensorSource;
              const _sensor = sensorsRef.current.find(
                (_s) => _s.sensorId === source.sensorId
              );
              const seconds = fixCollectionInterval(
                _sensor?.collectionInterval
              );
              return {
                key: edgeSourceStringifier(s),
                sensorId: source.sensorId,
                collectionInterval: { seconds },
                analytic: TimeSeriesData.makeAnalytic(
                  source.resolution,
                  source.readings
                ),
              };
            }),
          })
        );
      }
      if (simulationSources.length) {
        simulationRequests = getTableRequestChunks(simulationSources).map(
          (sources) =>
            TimeSeriesData.retrieve(
              signal,
              {
                ...baseRequestBody,
                sources: sources.map((s) => {
                  const source = s as SimulationSource;
                  return {
                    key: edgeSourceStringifier(s),
                    analysis_group_id: source.analysis_group_id,
                    attribute: source.attribute,
                    database_id: source.database_id,
                    result_id: source.result_id,
                    table_id: source.table_id,
                    object_id: source.object_id,
                    analytic: TimeSeriesData.makeAnalytic(
                      Resolutions.RAW,
                      Reading.Close
                    ),
                  };
                }),
              },
              { type: 'plot' },
              true // use V2
            )
        );
      }

      // Requests all batches in parallel
      const responses = await Promise.all([
        ...sensorRequests,
        ...simulationRequests,
      ]);

      // Merges the responses in a single object as if only one request was made.
      const response: TimeSeriesData.ResponseBody = {
        results: responses.reduce(
          (record, item) => Object.assign(record, item.data.results),
          {} as Record<string, TimeSeriesData.ResponseResultEntry>
        ),
      };
      return response;
    },
    [
      companySettings.timeZoneIANA,
      dataLimit,
      edgeSources,
      useV3,
      simulations,
      simulationSourceMapping,
    ]
  );

  const sensorDataRecords = React.useMemo(() => {
    const dataRecords: SensorDataRecords = {};

    seriesArray.forEach((series) => {
      if (!isSensorSeries(series) || series.customData) return;

      const dataEntry = seriesData.getEntry({
        sensorId: series.sensorId,
        resolution: series.resolution,
      });

      if (dataRecords[series.sensorId] === undefined) {
        dataRecords[series.sensorId] = {};
      }

      if (dataRecords[series.sensorId]?.[series.resolution] === undefined) {
        const _data = dataEntry.data
          ? Array.from(dataEntry.data.entries())
          : [];

        const timestamps: number[] = [];
        const measurements: SensorMeasureData = {};

        for (const [timestamp, measures] of _data) {
          timestamps.push(timestamp);
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          measurements[timestamp] = Array.from(measures.values());
        }

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        dataRecords[series.sensorId][series.resolution] = {
          status: dataEntry.status ?? 'idle',
          data:
            dataEntry.status === 'resolved'
              ? {
                  timestamps,
                  measurements,
                  unit: dataEntry.unit,
                }
              : null,
        };
      }
    });

    return dataRecords;
  }, [seriesArray, seriesData]);

  const edgeDataRecords = React.useMemo(() => {
    const r: SensorDataRecords = {};

    for (const s of edgeSources) {
      // Skip simulation sources that have been deleted and are not in the mapping
      if (
        isSimulationSource(s) &&
        !simulationSourceMapping.has(s.simulation_name)
      ) {
        if (!r[s.simulation_name]) r[s.simulation_name] = {};
        r[s.simulation_name][Resolutions.RAW] = {
          status: 'resolved',
          data: null,
        };
        continue;
      }
      // For simulation sources, use the updated source from mapping for data lookup
      const sourceForDataLookup = isSimulationSource(s)
        ? simulationSourceMapping.get(s.simulation_name) || s
        : s;
      const key = edgeSourceStringifier(sourceForDataLookup);
      const entry = edgeData?.results?.[key];

      const unit = entry?.unit ?? null;
      const timestamps: number[] = [];
      const measurements: SensorMeasureData = {};

      if (entry?.data?.length) {
        for (const [timestamp, ...values] of entry.data) {
          const m = new Array(6).fill(null) as EdgeData;
          timestamps.push(timestamp);

          if (isSimulationSource(s)) {
            const simValue = values[0]; // Simulation only contains one value
            measurements[timestamp] = m.map(
              () => simValue
            ) as SensorMeasureData[number];
            continue;
          }

          for (let i = 0; i < s.readings.length; i++) {
            const r = s.readings[i];
            const ri = readingIndex[r.toLowerCase()];
            m[ri] = values[i];
          }
          measurements[timestamp] = m;
        }
      }

      if (isSensorSource(s) && !r[s.sensorId]) r[s.sensorId] = {};
      if (isSimulationSource(s) && !r[s.simulation_name])
        r[s.simulation_name] = {};

      if (edgeStatus !== 'resolved') {
        if (isSensorSource(s)) {
          r[s.sensorId][s.resolution] = {
            status: edgeStatus,
            data: null,
          };
        }
        if (isSimulationSource(s)) {
          r[s.simulation_name][Resolutions.RAW] = {
            status: edgeStatus,
            data: null,
          };
        }
      } else {
        if (isSensorSource(s)) {
          r[s.sensorId][s.resolution] = {
            status: 'resolved',
            data: { timestamps, measurements, unit },
          };
        }
        if (isSimulationSource(s)) {
          r[s.simulation_name][Resolutions.RAW] = {
            status: 'resolved',
            data: { timestamps, measurements, unit },
          };
        }
      }
    }
    return r;
  }, [edgeData?.results, edgeSources, edgeStatus, simulationSourceMapping]);

  React.useEffect(() => {
    if (!isEdgeEnabled) return;
    if (!sensorsInitialized) return;
    if (edgeSources.some(isSimulationSource) && !simulations?.length) return;
    retrieveEdgeData({
      timeSelection: {
        from: props.timeRangeSelection?.min,
        to: props.timeRangeSelection?.max,
      },
    });
  }, [
    isEdgeEnabled,
    sensorsInitialized,
    retrieveEdgeData,
    props.timeRangeSelection?.min,
    props.timeRangeSelection?.max,
    simulations,
    edgeSources,
    simulationSourceMapping,
  ]);

  React.useEffect(() => {
    if (isEdgeEnabled) return;
    retrieve({
      start: props.timeRangeSelection?.min,
      end: props.timeRangeSelection?.max,
      limitFrom: 'end',
    });
  }, [
    isEdgeEnabled,
    retrieve,
    props.timeRangeSelection?.min,
    props.timeRangeSelection?.max,
  ]);

  return (
    <ChartingErrorBoundary chartProps={props}>
      <TableDashlet
        data={isEdgeEnabled ? edgeDataRecords : sensorDataRecords}
        simulationsStatus={simulationsStatus}
        page={props.page}
        sort={props.sort}
        series={props.series}
        readings={props.readings}
        showUnits={props.showUnits}
        showDateTime={props.showDateTime}
        onPageChange={props.onPageChange}
        onSortChange={props.onSortChange}
        useMostRecentReading={props.useMostRecentReading}
        density={props.density}
        onSelectionChange={props.onSelectionChange}
        conditionalFormatting={props.conditionalFormatting}
        columnConditionalFormatting={props.columnConditionalFormatting}
        onColumnSelectedChange={props.onColumnSelectedChange}
        showSelection={props.showSelection}
        showColumnSelection={props.showColumnSelection}
        showSensorAlias={props.showSensorAlias}
        clearSelection={props.clearSelection}
        uniqueRows={props.uniqueRows}
      />
    </ChartingErrorBoundary>
  );
};

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * Old Table
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

export const TableDashletV1 = (
  props: ConnectedTableDashletProps
): JSX.Element | null => {
  const seriesArray = !Array.isArray(props.series)
    ? [props.series]
    : props.series;

  const sensorDataRecords = useSensorData(
    seriesArray
      .filter((series): series is Series => isSensorSeries(series))
      .map((series) => ({
        sensorId: series.sensorId,
        resolution: series.resolution,
      })),
    props.dataRangeSelection
  );

  return (
    <ChartingErrorBoundary chartProps={props}>
      <TableDashlet
        data={sensorDataRecords}
        page={props.page}
        sort={props.sort}
        series={props.series}
        readings={props.readings}
        showUnits={props.showUnits}
        showDateTime={props.showDateTime}
        onPageChange={props.onPageChange}
        onSortChange={props.onSortChange}
        useMostRecentReading={props.useMostRecentReading}
        density={props.density}
        showSelection={props.showSelection}
        showColumnSelection={props.showColumnSelection}
      />
    </ChartingErrorBoundary>
  );
};

export default ConnectedTableDashlet;
